import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAssistant } from '../contexts/AssistantContext';
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  HeartIcon,
  ShareIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  GlobeAltIcon,
  CameraIcon,
  CalendarDaysIcon,
  PhoneIcon,
  EnvelopeIcon,
  PlusIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon, HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Tour, Review } from '../types';
import ReviewForm from '../components/Reviews/ReviewForm';
import BookingWizard from '../components/Booking/BookingWizard';
import ReviewCard from '../components/Reviews/ReviewCard';
import { tourService } from '../services/tourService';
import { usePricing } from '../contexts/PricingContext';
import { pricingService } from '../services/pricingService';
import { currencyService } from '../services/currencyService';
import { useAuth } from '../contexts/AuthContext';
import { bookingService } from '../services/bookingService';
import { accommodationService } from '../services/accommodationService';
import { AccommodationProvider } from '../types/accommodation';
import { useToast } from '../contexts/ToastContext';

import { PricingTier, TieredPackage, NationalityType, FinalPrice } from '../types';
import SEO from '../components/Common/SEO';
import CountdownTimer from '../components/Common/CountdownTimer';
import { isFuture, isPast } from '../utils/dateUtils';
import LiquidGlassModal from '../components/Common/LiquidGlassModal';
import StartPlanningButton from '../components/Common/StartPlanningButton';

const TourDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tour, setTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [isSlideshowPlaying, setIsSlideshowPlaying] = useState(true);
  const [slideshowInterval, setSlideshowInterval] = useState<NodeJS.Timeout | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextImageIndex, setNextImageIndex] = useState<number | null>(null);
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [showClosedModal, setShowClosedModal] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const [hasConfirmedBooking, setHasConfirmedBooking] = useState(false);
  const [isCheckingBooking, setIsCheckingBooking] = useState(false);
  const { showSuccess, showError } = useToast();
  
  const safeArray = (val: any): any[] => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim()) {
      if (val.startsWith('[') && val.endsWith(']')) {
        try { return JSON.parse(val); } catch (e) { return [val]; }
      }
      return val.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Synchronize scheduled modal with URL parameter if navigated from a tour card
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('show_scheduled') === 'true' && tour?.status === 'scheduled') {
      setShowScheduledModal(true);
    }
  }, [tour?.status, window.location.search]);

  // Persist Booking Wizard state via URL hash so it survives page reloads
  useEffect(() => {
    if (window.location.hash === '#book') {
      setIsBookingOpen(true);
    }
  }, []);

  useEffect(() => {
    if (isBookingOpen) {
      window.history.replaceState(null, '', '#book');
    } else {
      if (window.location.hash === '#book') {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }, [isBookingOpen]);

  const { trackTourView } = useAssistant();

  const pricingContext = usePricing();
  const [selectedTier, setSelectedTier] = useState<PricingTier>(pricingContext.tier);
  const [calculatedPricing, setCalculatedPricing] = useState<FinalPrice | null>(null);
  const [unitCalculatedPricing, setUnitCalculatedPricing] = useState<FinalPrice | null>(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);

  // Sync local tier with context tier when it changes (e.g. from the wizard)
  useEffect(() => {
    if (pricingContext.tier !== selectedTier) {
      setSelectedTier(pricingContext.tier);
    }
  }, [pricingContext.tier]);

  const [activeVariant, setActiveVariant] = useState<{
    itinerary: any[];
    inclusions: string[];
    exclusions: string[];
    guideClass: string;
    vehicleType: string;
  } | null>(null);
  const [allAccommodations, setAllAccommodations] = useState<AccommodationProvider[]>([]);
  const [selectedAccToShow, setSelectedAccToShow] = useState<AccommodationProvider | null>(null);

  // Dynamic Pricing Calculation & Tier Content Variants
  useEffect(() => {
    const calculatePriceAndContent = async () => {
      if (!tour) return;
      setIsLoadingPrice(true);
      try {
        const hasTierVariants = tour.tierVariants && Object.keys(tour.tierVariants).length > 0;
        let variants: { [key in PricingTier]: any };

        if (hasTierVariants) {
          variants = tour.tierVariants as any;
        } else {
          setCalculatedPricing(null);
          setUnitCalculatedPricing(null);
          setIsLoadingPrice(false);
          return;
        }

        const contextWithTier = { ...pricingContext, tier: selectedTier };

        const tieredPkg = {
          ...tour,
          name: tour.title,
          overview: tour.description,
          gallery: tour.images,
          durationDays: parseInt(tour.duration) || 1,
          destinations: [tour.location.name],
          status: 'PUBLISHED',
          tierVariants: variants
        };

        // 1. Calculate Cumulative Total
        const totalResult = await pricingService.calculatePackagePrice(tieredPkg, contextWithTier);
        setCalculatedPricing(totalResult);

        // 2. Calculate Unit Price (1 Adult)
        const unitContext = { ...contextWithTier, pax: { adults: 1, children: 0 } };
        const unitResult = await pricingService.calculatePackagePrice(tieredPkg, unitContext);
        setUnitCalculatedPricing(unitResult);

        // Set the active content variant based on selected tier
        setActiveVariant(variants[selectedTier]);
      } catch (error) {
        console.error('Error calculating tour price and content:', error);
      } finally {
        setIsLoadingPrice(false);
      }
    };

    calculatePriceAndContent();
  }, [tour, pricingContext, selectedTier]);

  useEffect(() => {
    const loadTour = async () => {
      if (id) {
        try {
          const foundTour = await tourService.getTourById(id);

          if (foundTour) {
            setTour(foundTour);
            // Track tour view for the Assistant
            trackTourView(foundTour);
          } else {
            console.error(`❌ TourDetails: Tour not found with ID: "${id}"`);
            // Don't redirect - let the component show "Tour not found" message
          }
        } catch (error) {
          console.error(`❌ TourDetails: Error loading tour (ID: "${id}"):`, error);
        }
      } else {
        console.error('❌ TourDetails: No tour ID provided in URL');
      }
      setLoading(false);
    };

    loadTour();
  }, [id]);

  useEffect(() => {
    const loadAllAccommodations = async () => {
      try {
        const accs = await accommodationService.getAllProviders();
        setAllAccommodations(accs);
      } catch (error) {
        console.error('Error loading accommodations for itinerary:', error);
      }
    };
    loadAllAccommodations();
  }, []);

  // Handle automatic date selection for scheduled safaris
  useEffect(() => {
    if (tour?.status === 'scheduled' && tour.safariStartDate) {
      pricingContext.setDates(tour.safariStartDate, tour.safariEndDate || '');
    }
  }, [tour?.id, tour?.status, tour?.safariStartDate]);

  // Listen for background tour updates specific to this tour
  useEffect(() => {
    const handleTourUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setTour(customEvent.detail);
      }
    };

    if (id) {
      window.addEventListener(`tourUpdated:${id}`, handleTourUpdated);
      return () => window.removeEventListener(`tourUpdated:${id}`, handleTourUpdated);
    }
  }, [id]);

  // Slideshow functionality with crossfade
  useEffect(() => {
    if (!tour || tour.images.length <= 1) return;

    let interval: NodeJS.Timeout | null = null;

    if (isSlideshowPlaying) {
      interval = setInterval(() => {
        setSelectedImage(prevIndex => {
          const nextIndex = (prevIndex + 1) % tour.images.length;
          setNextImageIndex(nextIndex);
          setIsTransitioning(true);

          // Start the crossfade transition
          setTimeout(() => {
            setNextImageIndex(null);
            setIsTransitioning(false);
          }, 500); // Half of the transition duration

          return nextIndex;
        });
      }, 4000); // Change image every 4 seconds

      setSlideshowInterval(interval);
    } else {
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
        setSlideshowInterval(null);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [tour, isSlideshowPlaying, tour?.images?.length]);

  // Cleanup slideshow on unmount
  useEffect(() => {
    return () => {
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
      }
    };
  }, []);

  // Load reviews when tour is loaded
  useEffect(() => {
    const loadReviews = async () => {
      if (id) {
        setReviewsLoading(true);
        try {
          const tourReviews = await tourService.getTourReviews(id);
          setReviews(tourReviews);
        } catch (error) {
          console.error('Error loading reviews:', error);
        } finally {
          setReviewsLoading(false);
        }
      }
    };

    if (tour) {
      loadReviews();
    }
  }, [id, tour]);

  // Check if user has a confirmed booking for this tour
  useEffect(() => {
    const checkBooking = async () => {
      if (isAuthenticated && user?.email && id) {
        setIsCheckingBooking(true);
        try {
          const userBookings = await bookingService.getBookingsByClient(user.email);
          const hasConfirmed = userBookings.some(
            (b: any) => b.tour_id === id && b.status?.toLowerCase() === 'confirmed'
          );
          setHasConfirmedBooking(hasConfirmed);
        } catch (error) {
          console.error('Error checking user bookings:', error);
          setHasConfirmedBooking(false);
        } finally {
          setIsCheckingBooking(false);
        }
      } else {
        setHasConfirmedBooking(false);
      }
    };

    checkBooking();
  }, [isAuthenticated, user?.email, id]);

  // URL Normalization: If visited via ID but slug exists, update URL without reload
  useEffect(() => {
    if (tour?.slug && id === tour.id) {
      const newPath = `/tours/${tour.slug}`;
      window.history.replaceState(null, '', newPath + window.location.search + window.location.hash);
    }
  }, [tour, id]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading tour details...</p>
        </div>
      </div>
    );
  }

  // If tour not found, show error message
  if (!tour) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Tour Not Found</h1>
          <p className="text-gray-600 mb-8">The tour you're looking for doesn't exist.</p>
          <a
            href="/tours"
            className="bg-primary-800 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
          >
            View All Tours
          </a>
        </div>
      </div>
    );
  }




  const handleShare = async () => {
    if (!tour) return;

    // Construct the absolute share URL using the slug if available
    const siteOrigin = window.location.origin;
    const shareUrl = tour.slug 
      ? `${siteOrigin}/tours/${tour.slug}`
      : `${siteOrigin}/tours/${tour.id}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${tour.title} | SightTrail Safaris`,
          text: tour.shortDescription || `Check out this amazing safari: ${tour.title}`,
          url: shareUrl,
        });
      } catch (err) {
        // Only log if it's not a user cancellation
        if ((err as Error).name !== 'AbortError') {
          console.log('Error sharing:', err);
        }
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        showSuccess('Link copied to clipboard!', 'Share Event');
      } catch (err) {
        console.log('Error copying to clipboard:', err);
      }
    }
  };


  const goToImage = (index: number) => {
    if (tour && !isTransitioning && index !== selectedImage) {
      // Pause slideshow when user manually selects an image
      setIsSlideshowPlaying(false);

      setNextImageIndex(index);
      setIsTransitioning(true);

      setTimeout(() => {
        setSelectedImage(index);
        setNextImageIndex(null);
        setIsTransitioning(false);

        // Resume slideshow after 3 seconds
        setTimeout(() => {
          setIsSlideshowPlaying(true);
        }, 3000);
      }, 500);
    }
  };

  const handleReviewSubmit = async (reviewData: {
    userName: string;
    rating: number;
    title: string;
    comment: string;
  }) => {
    if (!id) return;

    try {
      const newReview = await tourService.addReview(id, reviewData);
      setReviews(prev => [newReview, ...prev]);
      setShowReviewForm(false);

      // Update tour rating
      await tourService.updateTourRating(id);

      // Reload tour to get updated rating
      const updatedTour = await tourService.getTourById(id);
      if (updatedTour) {
        setTour(updatedTour);
      }

      showSuccess('Review submitted successfully!', 'Review Board');
    } catch (error) {
      console.error('Error submitting review:', error);
      showError('Failed to submit review. Please try again.', 'Submission Error');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'itinerary', label: 'Itinerary' },
    { id: 'includes', label: 'What\'s Included' },
    { id: 'requirements', label: 'Requirements' },
    { id: 'guide', label: 'Your Guide' },
    { id: 'reviews', label: 'Reviews' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {tour && (
        <SEO
          title={tour.title}
          description={tour.description || `Experience the ${tour.title} with expert Maasai guides.`}
          image={tour.images?.[0]}
          keywords={[...tour.highlights || [], tour.title, tour.category || '', 'safari', 'kenya', 'adventure']}
          structuredData={{
            "@type": "Product",
            "name": tour.title,
            "description": tour.description,
            "image": tour.images,
            "offers": {
              "@type": "Offer",
              "price": tour.price,
              "priceCurrency": tour.currency || "USD",
              "availability": tour.active ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
            },
            "aggregateRating": tour.rating ? {
              "@type": "AggregateRating",
              "ratingValue": tour.rating,
              "reviewCount": tour.reviewCount || 1
            } : undefined
          }}
        />
      )}
      {/* Floating Safari Info Card - Only the tour name section */}
      <div className="relative z-30 sm:animate-float-in pt-6 pb-6">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="floating-safari-card glass-effect sm:rounded-2xl overflow-hidden bg-white sm:bg-transparent border-b sm:border-none">
            <div className="px-4 sm:px-6 py-3 sm:py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Left: Safari Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-gradient-to-r from-primary-100 to-primary-50 text-primary-800 text-[10px] sm:text-xs font-medium border border-primary-200/50">
                      <SparklesIcon className="h-3 w-3 mr-1" />
                      Premium Safari
                    </div>
                    <div className="rating-container flex items-center bg-gradient-to-r from-yellow-50 to-amber-50 rounded-full px-2 py-0.5 border border-yellow-200/50">
                      {[...Array(5)].map((_, i) => (
                        <StarSolidIcon
                          key={i}
                          className={`rating-star h-3 w-3 ${i < Math.floor(tour.rating)
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                            }`}
                        />
                      ))}
                      <span className="ml-1 text-[10px] sm:text-xs text-gray-700 font-semibold">
                        {tour.rating} ({tour.reviewCount})
                      </span>
                    </div>
                  </div>

                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-2 truncate bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    {tour.title}
                  </h1>

                  {/* Compact Details Row */}
                  <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs md:text-sm text-gray-600">
                    <div className="flex items-center gap-1 bg-gray-50/80 rounded-lg px-2 py-1 whitespace-nowrap">
                      <MapPinIcon className="h-3 w-3 sm:h-4 w-4 text-primary-600" />
                      <span className="truncate max-w-[80px] sm:max-w-none font-medium">{tour.location.name}</span>
                    </div>
                    <div className="flex items-center gap-1 bg-gray-50/80 rounded-lg px-2 py-1 whitespace-nowrap">
                      <ClockIcon className="h-3 w-3 sm:h-4 w-4 text-primary-600" />
                      <span className="font-medium">{tour.duration}</span>
                    </div>
                    <div className="flex items-center gap-1 bg-gray-50/80 rounded-lg px-2 py-1 whitespace-nowrap">
                      <UsersIcon className="h-3 w-3 sm:h-4 w-4 text-primary-600" />
                      <span className="font-medium">Max {tour.maxParticipants}</span>
                    </div>
                  </div>
                </div>

                {/* Right: Price & Actions */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between sm:justify-end gap-3 sm:gap-6 border-t sm:border-t-0 border-gray-100 pt-4 sm:pt-0 w-full sm:w-auto mt-4 sm:mt-0">
                  {/* Pricing Block */}
                  <div className="flex flex-col items-start sm:items-end w-full sm:w-auto">
                      <div className="flex flex-col items-start sm:items-end">
                        <div className="flex items-center gap-3">
                          {unitCalculatedPricing?.isDiscounted && unitCalculatedPricing.originalTotal && (
                            <span className="text-lg sm:text-xl text-gray-400 line-through decoration-red-500/50 decoration-2 font-medium">
                              {currencyService.formatCurrency(unitCalculatedPricing.originalTotal, unitCalculatedPricing.currency)}
                            </span>
                          )}
                          <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-primary-900 whitespace-nowrap tracking-tight">
                            {isLoadingPrice ? '...' : (
                              unitCalculatedPricing
                                ? currencyService.formatCurrency(unitCalculatedPricing.total, unitCalculatedPricing.currency)
                                : tour.price
                                  ? currencyService.formatCurrency(tour.price, tour.currency || 'USD')
                                  : <span className="text-sm text-accent-600 uppercase">Pricing Pending</span>
                            )}
                          </div>
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-widest mt-0.5 whitespace-nowrap">
                          {unitCalculatedPricing ? `/ Adult • ${selectedTier} Tier` : (tour.price ? '/ Adult (Base)' : 'Contact Admin')}
                          {unitCalculatedPricing?.isDiscounted && (
                            <span className="ml-2 text-emerald-600 font-black animate-pulse">SPECIAL OFFER</span>
                          )}
                        </div>
                      </div>
                    {calculatedPricing && (
                      <div className="mt-2 inline-flex items-center px-3 py-1.5 rounded-full bg-gray-100 text-gray-800 text-[10px] sm:text-xs font-bold border border-gray-200 shadow-sm whitespace-nowrap shrink-0">
                        <span className="opacity-60 mr-1.5 uppercase tracking-wider font-black">Total</span>
                        {currencyService.formatCurrency(calculatedPricing.total, calculatedPricing.currency)}
                        <span className="ml-1.5 opacity-50">({pricingContext.pax.adults}A, {pricingContext.pax.children}C)</span>
                      </div>
                    )}
                  </div>

                  {/* Actions Block */}
                  <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-between sm:justify-start mt-4 sm:mt-0">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsFavorite(!isFavorite)}
                        className="p-2 sm:p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors shrink-0"
                        aria-label="Toggle favorite"
                      >
                        {isFavorite ? (
                          <HeartSolidIcon className="h-4 w-4 sm:h-5 w-5 text-red-500" />
                        ) : (
                          <HeartIcon className="h-4 w-4 sm:h-5 w-5 text-gray-600" />
                        )}
                      </button>

                      <button
                        onClick={handleShare}
                        className="p-2 sm:p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors shrink-0"
                        aria-label="Share tour"
                      >
                        <ShareIcon className="h-4 w-4 sm:h-5 w-5 text-gray-600" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 flex-1 sm:flex-none justify-end">
                      <StartPlanningButton label="Plan" wrapperClassName="shrink-0" />
                      <button
                        onClick={() => {
                          if (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) {
                            setShowScheduledModal(true);
                          } else if (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) {
                            setShowClosedModal(true);
                          } else {
                            setIsBookingOpen(true);
                          }
                        }}
                        disabled={['expired', 'deactivated', 'suspended'].includes(tour.status || '')}
                        className={`book-now-button text-white font-bold py-2.5 sm:py-3 px-4 sm:px-8 rounded-xl text-xs sm:text-sm transition-all sm:min-w-[140px] shadow-lg shrink-0 whitespace-nowrap ${['expired', 'deactivated', 'suspended'].includes(tour.status || '') || (tour.bookingCloseDate && isPast(tour.bookingCloseDate))
                          ? 'bg-gray-400 cursor-not-allowed opacity-70'
                          : 'hover:shadow-primary-200 hover:shadow-xl hover:-translate-y-0.5'
                          }`}
                      >
                        {tour.status === 'suspended' ? 'Soon' :
                          tour.status === 'expired' ? 'Expired' :
                            tour.status === 'deactivated' ? 'N/A' :
                              (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) ? 'Waitlist' :
                                (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) ? 'Closed' :
                                  'Book Now'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent opacity-50"></div>
          </div>
        </div>
      </div>

      {/* Mobile Booking Bar - Stick to bottom on mobile */}
      <div className="lg:hidden bg-white border-t border-gray-200 fixed bottom-0 left-0 right-0 z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-2.5 sm:py-3">
          <div className="flex items-center justify-between gap-2">

            {/* Left: price + /adult + share/like icons */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  {unitCalculatedPricing?.isDiscounted && unitCalculatedPricing.originalTotal && (
                    <span className="text-xs text-gray-400 line-through decoration-red-500/40">
                      {currencyService.formatCurrency(unitCalculatedPricing.originalTotal, unitCalculatedPricing.currency)}
                    </span>
                  )}
                  <div className="text-base sm:text-lg font-bold text-primary-800 whitespace-nowrap leading-none">
                    {isLoadingPrice ? '...' : (
                      unitCalculatedPricing
                        ? currencyService.formatCurrency(unitCalculatedPricing.total, unitCalculatedPricing.currency)
                        : tour.price
                          ? currencyService.formatCurrency(tour.price, tour.currency || 'USD')
                          : <span className="text-xs text-accent-600 uppercase">Pricing Pending</span>
                    )}
                  </div>
                </div>
                {calculatedPricing && (
                  <div className="text-[10px] text-gray-600 font-bold flex items-center gap-1 mt-0.5 whitespace-nowrap">
                    {calculatedPricing.isDiscounted && calculatedPricing.originalTotal && (
                      <span className="text-[8px] text-gray-400 line-through mr-1">
                        {currencyService.formatCurrency(calculatedPricing.originalTotal, calculatedPricing.currency)}
                      </span>
                    )}
                    <span>Total {currencyService.formatCurrency(calculatedPricing.total, calculatedPricing.currency)}</span>
                    <span className="text-[9px] opacity-70">({pricingContext.pax.adults}A, {pricingContext.pax.children}C)</span>
                  </div>
                )}
              </div>

              {/* Share & Like – compact icon buttons */}
              <div className="flex items-center gap-1 ml-1">
                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Toggle favourite"
                >
                  {isFavorite
                    ? <HeartSolidIcon className="h-4 w-4 text-red-500" />
                    : <HeartIcon className="h-4 w-4 text-gray-500" />
                  }
                </button>
                <button
                  onClick={handleShare}
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Share tour"
                >
                  <ShareIcon className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Right: Start Planning + Book Now */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <StartPlanningButton label="Plan" />
              <button
                onClick={() => {
                  if (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) {
                    setShowScheduledModal(true);
                  } else if (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) {
                    setShowClosedModal(true);
                  } else {
                    setIsBookingOpen(true);
                  }
                }}
                disabled={['expired', 'deactivated', 'suspended'].includes(tour.status || '') || !unitCalculatedPricing}
                className={`${['expired', 'deactivated', 'suspended'].includes(tour.status || '') || (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) || !unitCalculatedPricing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)
                    ? 'bg-primary-600 hover:bg-primary-500'
                    : 'bg-primary-800 hover:bg-primary-700'
                  } text-white font-bold py-2.5 px-4 rounded-lg transition-colors duration-200 text-xs min-h-[44px] flex-shrink-0`}
              >
                {!unitCalculatedPricing ? 'N/A' :
                  tour.status === 'suspended' ? 'Soon' :
                    tour.status === 'expired' ? 'Expired' :
                      tour.status === 'deactivated' ? 'N/A' :
                        (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) ? 'Waitlist' :
                          (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) ? 'Closed' :
                            'Book Now'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PricingFilter anchor - scrolled into view when Start Planning is tapped */}
      <div id="tour-details-pricing-filter" className="scroll-mt-4" />

      {/* Main Content - RESTORED ORIGINAL */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-6 sm:pb-8 md:pb-12">
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
          {/* Left Column - Tour Details */}
          <div className="lg:col-span-2 order-1 lg:order-1">
            {/* Image Gallery */}
            <div className="mb-4 sm:mb-6 md:mb-8 mt-0">
              <div className="relative h-48 sm:h-64 md:h-80 lg:h-96 rounded-2xl overflow-hidden mb-3 sm:mb-4 shadow-2xl group crossfade-container">
                {/* Current Image */}
                <img
                  src={tour.images[selectedImage]}
                  alt={tour.title}
                  className={`crossfade-image ${isTransitioning ? 'cinematic-crossfade-out' : 'current'
                    }`}
                />

                {/* Next Image (during transition) */}
                {nextImageIndex !== null && tour.images[nextImageIndex] && (
                  <img
                    src={tour.images[nextImageIndex]}
                    alt={tour.title}
                    className="crossfade-image cinematic-crossfade-in"
                  />
                )}

                {/* Cinematic Overlay */}
                <div className={`crossfade-overlay ${isTransitioning ? 'active' : ''}`}></div>

                {/* Enhanced Image Counter */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                  <CameraIcon className="h-4 w-4 mr-1" />
                  {selectedImage + 1} / {(tour.images?.length || 0)}
                </div>
              </div>

              {(tour.images?.length || 0) > 1 && (
                <div className="flex flex-wrap gap-3">
                  {(tour.images || []).map((image, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`relative w-20 h-14 sm:w-24 sm:h-16 rounded-xl overflow-hidden transition-all duration-300 touch-manipulation flex-shrink-0 shadow-lg hover:shadow-2xl ${selectedImage === index
                        ? 'ring-3 ring-primary-500 shadow-2xl scale-105'
                        : 'hover:scale-105'
                        }`}
                      style={{
                        transform: selectedImage === index ? 'scale(1.05)' : 'scale(1)',
                        boxShadow: selectedImage === index
                          ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 3px rgb(59 130 246)'
                          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <img
                        src={image}
                        alt={`${tour.title} ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{
                          borderRadius: '0.75rem'
                        }}
                      />
                      {selectedImage === index && (
                        <div
                          className="absolute inset-0 bg-primary-500/20 flex items-center justify-center rounded-xl backdrop-blur-sm"
                        >
                          <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg">
                            <CheckIcon className="w-4 h-4 text-primary-600" />
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Tab Navigation - Chip Style Responsive */}
            <div className="mb-4 sm:mb-6 relative group mt-2 sm:mt-0">
              <nav className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory flex-nowrap gap-2 sm:gap-3 pb-2 sm:pb-4 pt-1 sm:pt-2 px-0.5 sm:px-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative px-3.5 sm:px-4 py-1.5 sm:py-2 font-semibold text-[11px] sm:text-sm rounded-full transition-all duration-300 whitespace-nowrap flex items-center justify-center flex-shrink-0 snap-center shadow-md hover:shadow-lg hover:-translate-y-0.5 ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white ring-2 ring-blue-200 ring-offset-1 z-10'
                        : 'bg-white text-primary-900 border border-primary-200 hover:border-blue-300 hover:text-blue-600'
                    }`}
                  >
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>

              {/* Visual indicator for more items on mobile */}
              <div className="absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none lg:hidden opacity-100 group-hover:opacity-0 transition-opacity duration-300 z-10" />
              <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none lg:hidden opacity-0 group-hover:opacity-10 transition-opacity duration-300 z-10" />
            </div>

            {/* Tab Content */}
            <div className="space-y-6 sm:space-y-8">
              {activeTab === 'overview' && (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                      <GlobeAltIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      About This Safari
                    </h3>
                  </div>

                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-8">
                    {tour.description}
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-primary-50/50 rounded-xl p-6 border border-primary-100">
                      <div className="flex items-center mb-4">
                        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
                          <SparklesIcon className="h-5 w-5 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-primary-900">Safari Highlights</h4>
                      </div>
                      <ul className="space-y-3">
                        {safeArray(tour.highlights).map((highlight, index) => (
                          <li key={index} className="flex items-start">
                            <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                              <CheckIcon className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-gray-700 font-medium">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-primary-50/50 rounded-xl p-6 border border-primary-100">
                      <div className="flex items-center mb-4">
                        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
                          <MapPinIcon className="h-5 w-5 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-primary-900">Quick Facts</h4>
                      </div>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-2 border-b border-primary-100">
                          <span className="text-gray-600">Duration</span>
                          <span className="font-semibold text-primary-900">{tour.duration}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-primary-100">
                          <span className="text-gray-600">Group Size</span>
                          <span className="font-semibold text-primary-900">Max {tour.maxParticipants}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-primary-100">
                          <span className="text-gray-600">Location</span>
                          <span className="font-semibold text-primary-900">{tour.location?.name || 'Various'}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Rating</span>
                          <div className="flex items-center">
                            <div className="flex items-center mr-2">
                              {[...Array(5)].map((_, i) => (
                                <StarSolidIcon
                                  key={i}
                                  className={`h-4 w-4 ${i < Math.floor(tour.rating)
                                    ? 'text-yellow-400'
                                    : 'text-gray-300'
                                    }`}
                                />
                              ))}
                            </div>
                            <span className="font-semibold text-primary-900">{tour.rating}/5</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tier Selector in Overview */}
                  <div className="mt-8 pt-8 border-t border-primary-100">
                    <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                      <SparklesIcon className="h-6 w-6 text-primary-600 mr-2" />
                      Select Experience Tier
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                      {[PricingTier.LUXURY, PricingTier.STANDARD, PricingTier.BUDGET].map((tier) => (
                        <label
                          key={tier}
                          className={`cursor-pointer transition-all duration-200 ${selectedTier === tier
                            ? 'bg-primary-50 border-primary-600 ring-2 ring-primary-500 ring-opacity-50'
                            : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                            } border rounded-xl p-4 flex flex-col items-center justify-center text-center`}
                        >
                          <input
                            type="radio"
                            name="tier-overview"
                            value={tier}
                            checked={selectedTier === tier}
                            onChange={() => setSelectedTier(tier)}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded-full border-2 mb-2 ${selectedTier === tier ? 'border-primary-600 bg-primary-600' : 'border-gray-400'
                            }`}></div>
                          <span className={`font-bold text-sm ${selectedTier === tier ? 'text-primary-900' : 'text-gray-700'}`}>
                            {tier === PricingTier.LUXURY ? 'Luxury' : tier === PricingTier.STANDARD ? 'Standard' : 'Budget'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Price Breakdown Section */}
                  {calculatedPricing && (
                    <div className="mt-8 pt-8 border-t border-primary-100">
                      <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <SparklesIcon className="h-6 w-6 text-primary-600 mr-2" />
                        Price Breakdown ({selectedTier})
                      </h4>
                      <div className="space-y-4">
                        {(calculatedPricing?.breakdown || []).map((item: any, idx: number) => (
                          <div key={idx} className="flex justify-between items-start py-3 border-b border-neutral-100 last:border-0">
                            <div>
                              <div className="font-bold text-primary-900">{item.item}</div>
                              <div className="text-xs text-neutral-500">{item.description}</div>
                            </div>
                            <div className="font-bold text-primary-700">
                              {currencyService.formatCurrency(item.total, calculatedPricing.currency)}
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-4 mt-4 bg-primary-50 p-4 rounded-xl">
                          <div className="text-lg font-bold text-primary-900">Estimated Total</div>
                          <div className="text-2xl font-bold text-primary-800">
                            {currencyService.formatCurrency(calculatedPricing.total, calculatedPricing.currency)}
                          </div>
                        </div>
                        <p className="text-[10px] text-neutral-400 italic text-center mt-4">
                          *Final price may vary based on specific hotel availability and seasonal surcharges.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'itinerary' && (
                <div>
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                      <ClockIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      Daily Itinerary
                    </h3>
                  </div>

                  <div className="space-y-6">
                    {((activeVariant?.itinerary?.length ? activeVariant.itinerary : (tour.itinerary || []))).map((day, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                      >
                        <div className="p-6 sm:p-8">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mr-6">
                              <div className="w-16 h-16 bg-primary-600 text-white rounded-xl flex items-center justify-center text-xl font-bold">
                                {day.day}
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{day.title}</h4>
                              <p className="text-gray-700 mb-6 leading-relaxed">{day.description}</p>

                              {/* Activities */}
                              {(() => {
                                const activities = safeArray(day.activities);
                                return activities.length > 0 ? (
                                  <div className="mb-6">
                                    <h5 className="font-bold text-primary-600 mb-3 flex items-center text-lg">
                                      <div className="w-6 h-6 bg-primary-100 rounded-lg flex items-center justify-center mr-2">
                                        <ClockIcon className="h-4 w-4 text-primary-600" />
                                      </div>
                                      Activities
                                    </h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {activities.map((activity: any, i: number) => (
                                        <div
                                          key={i}
                                          className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg p-3"
                                        >
                                          <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 flex-shrink-0"></div>
                                          <span className="font-medium">{activity}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : null;
                              })()}

                              {/* Meals and Accommodation */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(() => {
                                  const mealsArr = safeArray(day.meals);
                                  return mealsArr.length > 0 ? (
                                    <div className="bg-blue-50/60 backdrop-blur-sm rounded-2xl p-5 border border-blue-100 shadow-lg shadow-blue-500/5 hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-1 transition-all duration-300">
                                      <h5 className="font-bold text-blue-900 mb-3 flex items-center">
                                        <div className="w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center mr-3 shadow-lg shadow-blue-500/20">
                                          <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                                          </svg>
                                        </div>
                                        Fine Dining
                                      </h5>
                                      <div className="flex flex-wrap gap-2">
                                        {mealsArr.map((meal: string, idx: number) => (
                                          <span key={idx} className="px-3 py-1 bg-white/80 text-xs font-bold text-blue-700 rounded-lg border border-blue-100 shadow-sm">
                                            {meal}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null;
                                })()}
                                {day.accommodation && (
                                  <div className="group bg-blue-50/60 backdrop-blur-sm rounded-2xl p-5 border border-blue-100 shadow-lg shadow-blue-500/5 hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-1 transition-all duration-300">
                                    <h5 className="font-bold text-blue-900 mb-3 flex items-center">
                                      <div className="w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center mr-3 shadow-lg shadow-blue-500/20">
                                        <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                      </div>
                                      Stay Experience
                                    </h5>
                                    <div className="flex flex-col gap-4">
                                      <div className="flex items-center justify-between">
                                        <p className="text-base font-black text-blue-950">
                                          {typeof day.accommodation === 'string' ? day.accommodation : (day.accommodation?.name || 'Standard Accommodation')}
                                        </p>
                                        <span className="px-2.5 py-1 bg-white/80 text-[10px] font-bold text-blue-600 rounded-full border border-blue-100 uppercase tracking-widest">
                                          Featured stay
                                        </span>
                                      </div>
                                      {(() => {
                                        const accName = typeof day.accommodation === 'string' ? day.accommodation : day.accommodation?.name;
                                        const provider = allAccommodations.find(a => 
                                          a.name.toLowerCase() === accName?.toLowerCase() || 
                                          a.id === day.accommodation?.providerId
                                        );
                                        
                                        return provider ? (
                                          <button 
                                            onClick={() => setSelectedAccToShow(provider)}
                                            className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs uppercase tracking-widest transition-all shadow-md shadow-blue-500/20 group-hover:shadow-lg group-hover:scale-[1.02]"
                                          >
                                            Explore Our Stay
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                          </button>
                                        ) : null;
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Map Integration for Location */}
                              {tour.location?.coordinates?.lat && tour.location?.coordinates?.lng && (
                                <div className="mt-6 bg-primary-50 rounded-xl p-4 border border-primary-200">
                                  <h5 className="font-bold text-primary-900 mb-3 flex items-center">
                                    <div className="w-6 h-6 bg-primary-500 rounded-lg flex items-center justify-center mr-2">
                                      <MapPinIcon className="h-4 w-4 text-white" />
                                    </div>
                                    Location Details
                                  </h5>
                                  <p className="text-sm text-primary-800 mb-3 font-medium">{tour.location.name}</p>
                                  <a
                                    href={`https://www.google.com/maps?q=${tour.location.coordinates.lat},${tour.location.coordinates.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                                  >
                                    <GlobeAltIcon className="h-4 w-4 mr-2" />
                                    View on Google Maps
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'includes' && (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8">
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                      <CheckIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      What's Included
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-primary-50 rounded-xl p-6 border border-primary-200">
                      <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center mr-3">
                          <CheckIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-primary-900">Included in Your Safari</h4>
                      </div>
                      <ul className="space-y-4">
                        {(() => {
                          const inclusions = safeArray(activeVariant?.inclusions?.length ? activeVariant.inclusions : tour.includes);
                          return inclusions.length > 0 ? (
                            inclusions.map((item: any, index: number) => (
                              <li key={index} className="flex items-start bg-white rounded-lg p-3">
                                <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                                  <CheckIcon className="h-4 w-4 text-white" />
                                </div>
                                <span className="text-gray-700 font-medium">{item}</span>
                              </li>
                            ))
                          ) : (
                            <p className="text-gray-500 italic">No inclusions specified.</p>
                          );
                        })()}
                      </ul>
                    </div>

                    <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                      <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center mr-3">
                          <XMarkIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-red-800">Not Included</h4>
                      </div>
                      <ul className="space-y-4">
                        {(() => {
                          const exclusions = safeArray(activeVariant?.exclusions?.length ? activeVariant.exclusions : tour.excludes);
                          return exclusions.length > 0 ? (
                            exclusions.map((item: any, index: number) => (
                              <li key={index} className="flex items-start bg-white rounded-lg p-3">
                                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                                  <XMarkIcon className="h-4 w-4 text-white" />
                                </div>
                                <span className="text-gray-700 font-medium">{item}</span>
                              </li>
                            ))
                          ) : (
                            <p className="text-gray-500 italic">No exclusions specified.</p>
                          );
                        })()}
                      </ul>
                    </div>
                  </div>
                </div>
              )}



              {activeTab === 'requirements' && (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8">
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                      <CheckIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      Requirements
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {(tour.requirements || []).length > 0 ? (
                      (tour.requirements || []).map((requirement, index) => (
                        <div
                          key={index}
                          className="flex items-start bg-primary-50 rounded-xl p-4 border border-primary-200"
                        >
                          <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <CheckIcon className="h-4 w-4 text-white" />
                          </div>
                          <span className="text-gray-700 font-medium">{requirement}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 italic text-center py-8">No specific requirements listed.</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'guide' && (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8">
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                      <UsersIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {(tour.guides && tour.guides.length > 0) ? 'Your Expert Guides' : 'Tour Guide'}
                    </h3>
                  </div>

                  {((tour.guides || []).length > 0) ? (
                    <div className="space-y-8">
                      {(tour.guides || []).map((guide, guideIndex) => (
                        <div key={guide.id || guideIndex} className={`${(tour.guides?.length || 0) > 1 ? 'pb-8 border-b border-gray-100 last:border-0 last:pb-0' : ''}`}>
                          <div className="flex flex-col sm:flex-row items-start space-y-6 sm:space-y-0 sm:space-x-8">
                            <div className="flex-shrink-0">
                              <img
                                src={guide.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'}
                                alt={`${guide.firstName} ${guide.lastName}`}
                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover shadow-lg border-4 border-white"
                                referrerPolicy="no-referrer"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                                <h4 className="text-2xl font-bold text-gray-900 mb-2 sm:mb-0">
                                  {guide.firstName} {guide.lastName}
                                </h4>
                                <div className="flex items-center bg-primary-50 rounded-full px-4 py-2">
                                  <div className="flex items-center mr-2">
                                    {[...Array(5)].map((_, i) => (
                                      <StarSolidIcon
                                        key={i}
                                        className={`h-4 w-4 ${i < Math.floor(guide.rating)
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                          }`}
                                      />
                                    ))}
                                  </div>
                                  <span className="text-sm font-bold text-gray-900">
                                    {guide.rating}/5.0
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mb-4 italic">
                                Rating based on performance across all assigned tours
                              </p>
                              <p className="text-gray-700 mb-6 leading-relaxed">
                                {guide.bio || 'Experienced safari guide with extensive knowledge of local wildlife and culture.'}
                              </p>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="bg-primary-50 rounded-xl p-4 border border-primary-200">
                                  <h5 className="font-bold text-primary-900 mb-2 flex items-center">
                                    <div className="w-6 h-6 bg-primary-600 rounded-lg flex items-center justify-center mr-2">
                                      <ClockIcon className="h-4 w-4 text-white" />
                                    </div>
                                    Experience
                                  </h5>
                                  <p className="text-primary-800 font-medium">{guide.experience || 'Professional safari guide'}</p>
                                </div>
                                <div className="bg-primary-50 rounded-xl p-4 border border-primary-200">
                                  <h5 className="font-bold text-primary-900 mb-2 flex items-center">
                                    <div className="w-6 h-6 bg-primary-600 rounded-lg flex items-center justify-center mr-2">
                                      <GlobeAltIcon className="h-4 w-4 text-white" />
                                    </div>
                                    Languages
                                  </h5>
                                  <p className="text-primary-800 font-medium">{guide.languages ? guide.languages.join(', ') : 'English, Swahili'}</p>
                                </div>
                                <div className="bg-primary-50 rounded-xl p-4 border border-primary-200">
                                  <h5 className="font-bold text-primary-900 mb-2 flex items-center">
                                    <div className="w-6 h-6 bg-primary-600 rounded-lg flex items-center justify-center mr-2">
                                      <SparklesIcon className="h-4 w-4 text-white" />
                                    </div>
                                    Specialties
                                  </h5>
                                  <p className="text-primary-800 font-medium">{guide.specialties ? guide.specialties.join(', ') : 'Wildlife photography, Bird watching'}</p>
                                </div>
                                <div className="bg-primary-50 rounded-xl p-4 border border-primary-200">
                                  <h5 className="font-bold text-primary-900 mb-2 flex items-center">
                                    <div className="w-6 h-6 bg-primary-600 rounded-lg flex items-center justify-center mr-2">
                                      <CheckIcon className="h-4 w-4 text-white" />
                                    </div>
                                    Certifications
                                  </h5>
                                  <p className="text-primary-800 font-medium">{guide.certifications ? guide.certifications.join(', ') : 'Licensed Professional Guide'}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-24 h-24 bg-amber-50 rounded-2xl mx-auto mb-6 flex items-center justify-center border border-amber-100">
                        <UsersIcon className="h-12 w-12 text-amber-400" />
                      </div>
                      <h4 className="text-xl font-bold text-gray-800 mb-3">This Tour Is Not Guided</h4>
                      <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
                        This is a self-guided safari experience — the itinerary, accommodations, and logistics are fully arranged for you, allowing you to explore at your own pace. All necessary instructions and materials will be provided before your departure.
                      </p>
                      <div className="mt-6 inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium border border-amber-100">
                        <SparklesIcon className="h-4 w-4" />
                        Fully Self-Guided Experience
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div>
                  <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mr-4">
                        <StarSolidIcon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                        Guest Reviews
                      </h3>
                    </div>
                    {isAuthenticated ? (
                      hasConfirmedBooking ? (
                        <button
                          onClick={() => setShowReviewForm(true)}
                          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-bold transition-colors duration-200"
                        >
                          Write a Review
                        </button>
                      ) : (
                        <div className="text-sm text-amber-600 font-medium bg-amber-50 px-4 py-2 rounded-lg border border-amber-100 flex items-center">
                          <CheckIcon className="h-4 w-4 mr-2" />
                          Only guests with confirmed bookings can write reviews
                        </div>
                      )
                    ) : (
                      <button
                        onClick={() => window.location.href = `/login?redirect=/tours/${id}`}
                        className="bg-primary-100 hover:bg-primary-200 text-primary-800 px-6 py-3 rounded-lg font-bold transition-colors duration-200"
                      >
                        Log in to Review
                      </button>
                    )}
                  </div>

                  {showReviewForm && (
                    <div className="mb-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                      <ReviewForm
                        tourId={id || ''}
                        onSubmit={handleReviewSubmit}
                        onCancel={() => setShowReviewForm(false)}
                      />
                    </div>
                  )}

                  {reviewsLoading ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg border border-gray-200">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <p className="text-gray-600 font-medium">Loading reviews...</p>
                    </div>
                  ) : reviews.length > 0 ? (
                    <div className="space-y-6">
                      {reviews.map((review, index) => (
                        <div key={review.id}>
                          <ReviewCard review={review} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl shadow-lg border border-gray-200">
                      <div className="w-24 h-24 bg-primary-50 rounded-xl mx-auto mb-6 flex items-center justify-center">
                        <StarSolidIcon className="h-12 w-12 text-primary-400" />
                      </div>
                      <p className="text-gray-600 mb-6 text-lg">No reviews yet. Be the first to share your safari experience!</p>
                      <button
                        onClick={() => setShowReviewForm(true)}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-bold transition-colors duration-200"
                      >
                        Write the First Review
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Booking Sidebar */}
          <div className="hidden lg:block lg:col-span-1 order-2 lg:order-2">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 lg:sticky lg:top-40">
              <div className="text-center mb-6">
                <div className="text-3xl sm:text-4xl font-bold text-primary-800 mb-2">
                  {isLoadingPrice ? '...' : (
                    unitCalculatedPricing
                      ? currencyService.formatCurrency(unitCalculatedPricing.total, unitCalculatedPricing.currency)
                      : <span className="text-xl text-accent-600 uppercase">Pricing Pending</span>
                  )}
                </div>
                <div className="text-gray-600 font-medium">
                  {unitCalculatedPricing ? 'per adult' : 'Contact Admin'}
                </div>

                {calculatedPricing && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-200 flex flex-col items-center">
                    <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-1 shadow-sm px-3 py-1 bg-white rounded-full border border-gray-100">Cumulative Total</div>
                    <div className="text-2xl font-black text-gray-900 leading-tight">
                      {currencyService.formatCurrency(calculatedPricing.total, calculatedPricing.currency)}
                    </div>
                    <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mt-1">
                      For {pricingContext.pax.adults} {pricingContext.pax.adults === 1 ? 'Adult' : 'Adults'}
                      {pricingContext.pax.children > 0 && ` & ${pricingContext.pax.children} ${pricingContext.pax.children === 1 ? 'Child' : 'Children'}`}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-center mt-4 bg-yellow-50 rounded-lg px-4 py-3">
                  <div className="flex items-center mr-3">
                    {[...Array(5)].map((_, i) => (
                      <StarSolidIcon
                        key={i}
                        className={`h-4 w-4 ${i < Math.floor(tour.rating)
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                          }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-bold text-gray-800">
                    {tour.rating} ({tour.reviewCount} reviews)
                  </span>
                </div>
              </div>

              <div className="flex flex-col xl:flex-row gap-3 mb-4">
                <StartPlanningButton
                  label="Plan Safari"
                  wrapperClassName="w-full xl:flex-1 h-full"
                  className="w-full h-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl font-bold py-4 px-4 rounded-xl transition-colors duration-200 text-sm sm:text-base outline-none"
                />
                <button
                  onClick={() => {
                    if (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) {
                      setShowScheduledModal(true);
                    } else if (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) {
                      setShowClosedModal(true);
                    } else {
                      setIsBookingOpen(true);
                    }
                  }}
                  disabled={['expired', 'deactivated', 'suspended'].includes(tour.status || '') || !unitCalculatedPricing}
                  className={`w-full xl:flex-1 flex items-center justify-center ${['expired', 'deactivated', 'suspended'].includes(tour.status || '') || (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) || !unitCalculatedPricing
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)
                      ? 'bg-primary-600 hover:bg-primary-500 text-white shadow-lg'
                      : 'bg-primary-800 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
                    } font-bold py-4 px-4 rounded-xl transition-colors duration-200 text-sm sm:text-base`}
                >
                  {!unitCalculatedPricing ? 'Unavailable' :
                    tour.status === 'suspended' ? 'Coming Soon' :
                      tour.status === 'expired' ? 'Expired' :
                        tour.status === 'deactivated' ? 'Unavailable' :
                          (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) ? 'Waitlist' :
                            (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) ? 'Closed' :
                              'Book Now'}
                </button>
              </div>

              {/* Countdown Displays */}
              {tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate) && (
                <div className="mb-6 animate-pulse">
                  <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 text-center">
                    <p className="text-xs font-bold uppercase tracking-widest text-primary-600 mb-2">Booking Opens In</p>
                    <CountdownTimer
                      targetDate={tour.bookingOpenDate}
                      className="text-primary-800"
                    />
                  </div>
                </div>
              )}

              {tour.status === 'active' && tour.bookingCloseDate && isFuture(tour.bookingCloseDate) && (
                <div className="mb-6">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-2">Booking Closes In</p>
                    <CountdownTimer
                      targetDate={tour.bookingCloseDate}
                      className="text-red-800"
                    />
                  </div>
                </div>
              )}

              <div className="text-center bg-primary-50 rounded-lg p-4 border border-primary-100 mb-6">
                <div className="flex items-center justify-center mb-2">
                  <CheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                  <span className="font-bold text-primary-900">Free Cancellation</span>
                </div>
                <p className="text-sm text-primary-700">
                  Cancel up to 24 hours before departure for a full refund
                </p>
              </div>

              <div className="space-y-3">
                {[
                  'Instant confirmation',
                  'Expert local guide included',
                  'Small group experience'
                ].map((perk, index) => (
                  <div key={perk} className="flex items-center text-gray-600 bg-gray-50 rounded-lg p-3">
                    <CheckIcon className="h-4 w-4 text-primary-500 mr-3 flex-shrink-0" />
                    <span className="font-medium">{perk}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Booking Section */}
        <div className="lg:hidden mt-8">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="text-center mb-6">
              <div className="text-3xl font-bold text-primary-800 mb-2">
                {isLoadingPrice ? '...' : (
                  unitCalculatedPricing
                    ? currencyService.formatCurrency(unitCalculatedPricing.total, unitCalculatedPricing.currency)
                    : <span className="text-xl text-accent-600 uppercase">Pricing Pending</span>
                )}
              </div>
              <div className="text-gray-600 font-medium whitespace-nowrap">
                {unitCalculatedPricing ? `per adult • ${pricingContext.pax.adults}A, ${pricingContext.pax.children}C` : 'Contact Admin'}
              </div>

              {calculatedPricing && (pricingContext.pax.adults > 1 || pricingContext.pax.children > 0) && (
                <div className="mt-4 p-3 bg-primary-50 rounded-lg border border-primary-100">
                  <div className="text-xl font-bold text-primary-900">
                    Total {currencyService.formatCurrency(calculatedPricing.total, calculatedPricing.currency)}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-center mt-4 bg-yellow-50 rounded-lg px-4 py-3">
                <div className="flex items-center mr-3">
                  {[...Array(5)].map((_, i) => (
                    <StarSolidIcon
                      key={i}
                      className={`h-4 w-4 ${i < Math.floor(tour.rating)
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                        }`}
                    />
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-800">
                  {tour.rating} ({tour.reviewCount} reviews)
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <StartPlanningButton
                label="Plan"
                wrapperClassName="w-full h-full"
                className="w-full h-full flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl font-bold py-3.5 px-3 rounded-lg transition-colors duration-200 text-sm sm:text-base outline-none"
              />
              <button
                onClick={() => {
                  if (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) {
                    setShowScheduledModal(true);
                  } else if (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) {
                    setShowClosedModal(true);
                  } else {
                    setIsBookingOpen(true);
                  }
                }}
                disabled={['expired', 'deactivated', 'suspended'].includes(tour.status || '')}
                className={`w-full ${['expired', 'deactivated', 'suspended'].includes(tour.status || '') || (tour.bookingCloseDate && isPast(tour.bookingCloseDate))
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)
                    ? 'bg-primary-600 hover:bg-primary-500 text-white shadow-lg'
                    : 'bg-primary-800 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
                  } font-bold py-3.5 px-3 rounded-lg transition-colors duration-200 text-sm sm:text-base`}
              >
                {tour.status === 'suspended' ? 'Soon' :
                  tour.status === 'expired' ? 'Expired' :
                    tour.status === 'deactivated' ? 'Unavailable' :
                      (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) ? 'Waitlist' :
                        (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) ? 'Closed' :
                          'Book Now'}
              </button>
            </div>

            <div className="text-center bg-primary-50 rounded-lg p-4 border border-primary-100">
              <div className="flex items-center justify-center mb-2">
                <CheckIcon className="h-4 w-4 text-primary-600 mr-2" />
                <span className="text-sm font-bold text-primary-900">Free Cancellation</span>
              </div>
              <p className="text-xs text-primary-700">
                Cancel up to 24 hours before departure
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Wizard */}
      <BookingWizard
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        tour={tour}
      />

      {/* Enhanced Accommodation Details Modal */}
      <LiquidGlassModal
        isOpen={!!selectedAccToShow}
        onClose={() => setSelectedAccToShow(null)}
        title={selectedAccToShow?.name}
        maxWidth="max-w-5xl"
      >
        {selectedAccToShow && (
          <div className="space-y-10 max-h-[80vh] overflow-y-auto pr-3 custom-scrollbar -mx-2 px-2">
            
            {/* Immersive Hero Section */}
            <div className="relative h-[400px] sm:h-[450px] -mt-2 rounded-[2rem] overflow-hidden shadow-2xl group">
              <img 
                src={selectedAccToShow.imageUrl || (selectedAccToShow.images?.[0])} 
                alt={selectedAccToShow.name} 
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-950/80 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-8 left-8 right-8 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <span className="px-4 py-1.5 bg-blue-600/90 backdrop-blur-md text-white text-[10px] font-black rounded-full shadow-lg uppercase tracking-[0.2em] border border-blue-400/30">
                      {selectedAccToShow.type}
                    </span>
                    {selectedAccToShow.isFeatured && (
                      <span className="px-4 py-1.5 bg-amber-500/90 backdrop-blur-md text-white text-[10px] font-black rounded-full shadow-lg uppercase tracking-[0.2em] border border-amber-400/30">
                        Featured Stay
                      </span>
                    )}
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight drop-shadow-xl capitalize">
                    {selectedAccToShow.name}
                  </h2>
                </div>
                
                <div className="flex flex-col items-start sm:items-end">
                   <span className="text-[10px] font-bold text-blue-200 uppercase tracking-widest mb-1">Starting from</span>
                   <div className="flex items-baseline gap-1.5">
                      <span className="text-blue-400 text-sm font-bold">USD</span>
                      <span className="text-4xl font-black text-white">
                        {typeof selectedAccToShow.basePricePerNight === 'number' ? selectedAccToShow.basePricePerNight.toLocaleString() : '---'}
                      </span>
                      <span className="text-blue-300 text-sm">/night</span>
                   </div>
                </div>
              </div>
            </div>

            {/* Content & Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              
              {/* Left Column: Story & Amenities */}
              <div className="lg:col-span-8 space-y-12">
                
                {/* DescriptionSection */}
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-2xl border border-blue-100">
                    <SparklesIcon className="w-5 h-5 text-blue-600" />
                    <h4 className="text-sm font-black uppercase tracking-[0.15em] text-blue-900">The Experience</h4>
                  </div>
                  <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed font-medium text-justify">
                    {selectedAccToShow.description || 'Step into a world of unparalleled luxury and natural beauty. This curated stay offers the perfect blend of modern comfort and authentic safari adventure.'}
                  </p>
                </div>

                {/* Amenities Section */}
                {selectedAccToShow.amenities && selectedAccToShow.amenities.length > 0 && (
                  <div className="space-y-6">
                    <h4 className="text-xs font-black uppercase tracking-[0.15em] text-neutral-400">Premium Amenities</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {selectedAccToShow.amenities.map(amenity => (
                        <div 
                          key={amenity}
                          className="flex items-center gap-3 p-4 bg-white dark:bg-slate-800/50 rounded-2xl border border-neutral-100 dark:border-slate-700/50 shadow-sm hover:shadow-md hover:border-blue-200 transition-all group"
                        >
                          <div className="w-2 h-2 rounded-full bg-blue-500 group-hover:scale-125 transition-transform" />
                          <span className="text-[13px] font-bold text-neutral-700 dark:text-neutral-300 capitalize">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gallery Grid */}
                {selectedAccToShow.images && selectedAccToShow.images.length > 0 && (
                  <div className="space-y-6 pt-4">
                    <h4 className="text-xs font-black uppercase tracking-[0.15em] text-neutral-400">Visual Tour</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {selectedAccToShow.images.map((img, idx) => (
                        <div 
                          key={idx} 
                          className={`group relative overflow-hidden rounded-2xl shadow-md cursor-pointer ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                        >
                          <img 
                            src={img} 
                            alt={`${selectedAccToShow.name} gallery ${idx}`} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-blue-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                             <div className="p-3 rounded-full bg-white/20 backdrop-blur-md">
                                <PlusIcon className="w-6 h-6 text-white" />
                             </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Location & Practicality */}
              <div className="lg:col-span-4 space-y-8">
                
                {/* Location Card */}
                <div className="bg-neutral-50 dark:bg-slate-900/50 rounded-[2rem] p-8 border border-neutral-200/50 dark:border-slate-800/50 space-y-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <MapPinIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-neutral-900 dark:text-white mb-2">Location</h4>
                    <div className="space-y-2 text-sm text-neutral-500 dark:text-neutral-400 font-medium">
                      <p className="text-blue-600 font-bold">{selectedAccToShow.locationName}</p>
                      <p>{selectedAccToShow.location?.address}</p>
                      <p>{selectedAccToShow.location?.region}, {selectedAccToShow.location?.country}</p>
                    </div>
                  </div>
                  
                  <button className="w-full py-4 bg-white dark:bg-slate-800 rounded-xl border border-neutral-200 dark:border-slate-700 text-xs font-black uppercase tracking-widest text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-slate-700 transition-all">
                    Show on map
                  </button>
                </div>

                {/* Contact Section */}
                <div className="px-2 space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-neutral-400">Assistance</h4>
                  <div className="space-y-3">
                    {selectedAccToShow.contactInfo?.phone && (
                      <div className="flex items-center gap-4 p-4 rounded-2xl bg-white dark:bg-slate-800/20 border border-neutral-100 dark:border-slate-800 shadow-sm hover:border-green-200 transition-all">
                        <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                          <PhoneIcon className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold text-neutral-400 uppercase">Direct Line</span>
                          <span className="text-sm font-black text-neutral-800 dark:text-neutral-200">{selectedAccToShow.contactInfo.phone}</span>
                        </div>
                      </div>
                    )}
                    {selectedAccToShow.contactInfo?.email && (
                      <div className="flex items-center gap-4 p-4 rounded-2xl bg-white dark:bg-slate-800/20 border border-neutral-100 dark:border-slate-800 shadow-sm hover:border-blue-200 transition-all">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                          <EnvelopeIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold text-neutral-400 uppercase">Email Guest Relations</span>
                          <span className="text-sm font-black text-neutral-800 dark:text-neutral-200 truncate max-w-[180px]">{selectedAccToShow.contactInfo.email}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Secure Booking Badge */}
                <div className="p-6 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-3xl text-white shadow-xl shadow-blue-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <ShieldCheckIcon className="w-6 h-6 text-indigo-200" />
                    <span className="text-xs font-black uppercase tracking-widest">SightTrail Verified</span>
                  </div>
                  <p className="text-[13px] leading-relaxed text-indigo-100 font-medium">
                    This property has been personally inspected by our team to guarantee world-class standards and safety.
                  </p>
                </div>

              </div>
            </div>
          </div>
        )}
      </LiquidGlassModal>

      {/* Lifecycle Modals */}
      <LiquidGlassModal
        isOpen={showScheduledModal}
        onClose={() => setShowScheduledModal(false)}
        title="Booking Opening Soon"
      >
        <div className="text-center">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CalendarDaysIcon className="h-10 w-10 text-primary-600" />
          </div>
          <p className="text-lg leading-relaxed mb-6">
            Booking for the <span className="font-bold text-primary-700">{tour.title}</span> is currently scheduled to open on:
          </p>
          <div className="bg-primary-50 border border-primary-100 rounded-2xl p-6 mb-8">
            <p className="text-2xl font-bold text-primary-800 mb-2">
              {tour.bookingOpenDate ? new Date(tour.bookingOpenDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'TBA'}
            </p>
            <div className="mt-4 border-t border-primary-200 pt-4">
              <CountdownTimer
                targetDate={tour.bookingOpenDate || ''}
                className="text-primary-700"
              />
            </div>
          </div>
          <button
            onClick={() => setShowScheduledModal(false)}
            className="w-full bg-primary-800 hover:bg-primary-700 text-white font-bold py-4 px-8 rounded-xl transition-all shadow-lg hover:shadow-primary-200"
          >
            Notify Me When It Opens
          </button>
        </div>
      </LiquidGlassModal>

      <LiquidGlassModal
        isOpen={showClosedModal}
        onClose={() => setShowClosedModal(false)}
        title="Booking Closed"
      >
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XMarkIcon className="h-10 w-10 text-red-600" />
          </div>
          <p className="text-lg leading-relaxed mb-8">
            We're sorry! Booking for this safari package has officially closed.
            Please check out our other available tours or contact us for upcoming dates.
          </p>
          <div className="flex gap-4">
            <button
              onClick={() => window.location.href = '/tours'}
              className="flex-1 bg-primary-800 hover:bg-primary-700 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg"
            >
              Browse Tours
            </button>
            <button
              onClick={() => setShowClosedModal(false)}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-4 px-6 rounded-xl transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </LiquidGlassModal>
    </div>
  );
};

export default TourDetails;